import { Subject } from 'rxjs';
import WaveformData from 'waveform-data';

import { AudioEditor } from './AudioEditor';
import { peach, roundRect } from './Draw';
import Waveform from './Waveform';

const SPACE_BOTTOM = 30;
const SPACE_TOP = 30;

const PRECISION_ARROUND = 8;

function scale(y: number, height: number, padding: number) {
  return ((128 - y) / 256) * (height - padding) + padding / 2;
}

function formatNumber(number: number) {
  if (number.toString().length === 2) {
    return number;
  }

  return `0${number}`;
}

const steps = [1, 2, 5, 10, 30, 60, 300, 600, 1800, 3600];

export default class ZoomWaveform extends Waveform {
  private width: number;
  private offset = 0;
  private hasClicked = false;
  private initialX = 0;
  // private prevX = 0;

  constructor(
    audioEditor: AudioEditor,
    private readonly viewDraggingSubject: Subject<{ from: number; to: number }>,
    private readonly waveformZoomSubject: Subject<{ width: number; zoom: number }>,
  ) {
    super(audioEditor, audioEditor.options.zoom);
    this.width = this.ctx.canvas.clientWidth;

    let closeToStart = false;
    let closeToEnd = false;
    // let insideSegment = false;
    let closeToMix = false;
    let closeToOutro = false;
    let closeToIntro = false;
    // let closeToCursor = false;

    audioEditor.onViewDragged$.subscribe(({ from }) => {
      this.offset = this.timeToPixels(from);
      this.refresh();
    });

    audioEditor.onWaveformZoom$.subscribe(({ width }) => {
      const middle = this.pixelsToTime(this.offset + this.ctx.canvas.width / 2);
      this.data = this.audioEditor.waveformData.resample({ width });

      const middlePx = this.timeToPixels(middle);
      const from = this.pixelsToTime(middlePx - this.ctx.canvas.width / 2);
      const to = this.pixelsToTime(middlePx + this.ctx.canvas.width / 2);

      this.viewDraggingSubject.next({ from, to });
    });

    this.ctx.canvas.addEventListener('pointerdown', event => {
      event.preventDefault();
      this.ctx.canvas.setPointerCapture(event.pointerId);

      this.hasClicked = true;
      this.initialX = event.offsetX;
      // this.prevX = event.offsetX;

      const segmentStartX =
        this.timeToPixels(audioEditor.selectionBox.from) - this.offset;
      const segmentEndX = this.timeToPixels(audioEditor.selectionBox.to) - this.offset;
      const mixX = this.timeToPixels(audioEditor.segmentBar.mixPoint) - this.offset;
      const introX = this.timeToPixels(audioEditor.segmentBar.introPoint) - this.offset;
      const outroX = this.timeToPixels(audioEditor.segmentBar.outroPoint) - this.offset;
      // const cursorX = this.timeToPixels(audioEditor.player.time) - this.offset;
      closeToStart = Math.abs(this.initialX - segmentStartX) < PRECISION_ARROUND;
      closeToEnd = Math.abs(this.initialX - segmentEndX) < PRECISION_ARROUND;
      closeToMix = Math.abs(this.initialX - mixX) < PRECISION_ARROUND;
      closeToIntro = Math.abs(this.initialX - introX) < PRECISION_ARROUND;
      closeToOutro = Math.abs(this.initialX - outroX) < PRECISION_ARROUND;
      // closeToCursor = Math.abs(this.initialX - cursorX) < PRECISION_ARROUND;

      // insideSegment = this.initialX < segmentEndX && this.initialX > segmentStartX;
    });

    this.ctx.canvas.addEventListener('pointerup', event => {
      event.preventDefault();

      this.hasClicked = false;
      if (this.initialX !== event.offsetX) {
        return;
      }

      // You performed a simple click
      const time = this.pixelsToTime(event.offsetX + this.offset);
      audioEditor.player.setTime(time);

      // this.prevX = event.offsetX;
      this.ctx.canvas.style.cursor = 'grab';
    });

    this.ctx.canvas.addEventListener('pointermove', event => {
      event.preventDefault();

      const segmentStartX =
        this.timeToPixels(audioEditor.selectionBox.from) - this.offset;
      const segmentEndX = this.timeToPixels(audioEditor.selectionBox.to) - this.offset;
      const mixX = this.timeToPixels(audioEditor.segmentBar.mixPoint) - this.offset;
      const introX = this.timeToPixels(audioEditor.segmentBar.introPoint) - this.offset;
      const outroX = this.timeToPixels(audioEditor.segmentBar.outroPoint) - this.offset;
      const cursorX = this.timeToPixels(audioEditor.player.time) - this.offset;

      if (!this.hasClicked) {
        if (
          Math.abs(event.offsetX - segmentStartX) < PRECISION_ARROUND ||
          (audioEditor.segmentBar.isMixActive &&
            Math.abs(event.offsetX - mixX) < PRECISION_ARROUND) ||
          (audioEditor.segmentBar.isIntroActive &&
            Math.abs(event.offsetX - introX) < PRECISION_ARROUND) ||
          (audioEditor.segmentBar.isOutroActive &&
            Math.abs(event.offsetX - outroX) < PRECISION_ARROUND) ||
          Math.abs(event.offsetX - segmentEndX) < PRECISION_ARROUND ||
          Math.abs(event.offsetX - cursorX) < PRECISION_ARROUND
        ) {
          this.ctx.canvas.style.cursor = 'col-resize';
        } else {
          this.ctx.canvas.style.cursor = 'crosshair';
        }
      }

      if (!this.hasClicked || this.initialX === event.offsetX) {
        return;
      }
      if (closeToOutro && this.audioEditor.segmentBar.isOutroActive) {
        this.audioEditor.segmentBar.setOutroPoint(
          this.pixelsToTime(event.offsetX + this.offset),
        );
      } else if (closeToIntro && this.audioEditor.segmentBar.isIntroActive) {
        this.audioEditor.segmentBar.setIntroPoint(
          this.pixelsToTime(event.offsetX + this.offset),
        );
      } else if (closeToMix && this.audioEditor.segmentBar.isMixActive) {
        this.audioEditor.segmentBar.setMixPoint(
          this.pixelsToTime(event.offsetX + this.offset),
        );
      } else if (closeToStart) {
        audioEditor.selectionBox.setStart(this.pixelsToTime(event.offsetX + this.offset));
      } else if (closeToEnd) {
        audioEditor.selectionBox.setEnd(this.pixelsToTime(event.offsetX + this.offset));
      } else {
        const time = this.pixelsToTime(event.offsetX + this.offset);
        audioEditor.player.setTime(time);
      }
      /*
    else if (insideSegment) {
      const dt = this.pixelsToTime(this.prevX - event.offsetX);
      this.audioEditor.selectionBox.from -= dt;
      this.audioEditor.selectionBox.to -= dt;

      audioEditor.selectionBox.setStart(this.audioEditor.selectionBox.from);
      audioEditor.selectionBox.setEnd(this.audioEditor.selectionBox.to);

      } else {
        // dragging the view
        const dx = this.prevX - event.offsetX;
        let fromX = this.offset + dx;
        let toX = fromX + this.ctx.canvas.width;

        if (this.pixelsToTime(toX - this.ctx.canvas.width / 2) > this.data.duration) {
          toX = this.timeToPixels(this.data.duration) + this.ctx.canvas.width / 2;
          fromX = toX - this.ctx.canvas.width;
        }

        if (this.pixelsToTime(fromX + this.ctx.canvas.width / 2) < 0) {
          fromX = -this.ctx.canvas.width / 2;
          toX = fromX + this.ctx.canvas.width;
        }

        const from = this.pixelsToTime(fromX);
        const to = this.pixelsToTime(toX);

        // audioEditor.emit('waveform.viewdragging', { from, to });
      }
       */

      // this.prevX = event.offsetX;
    });
  }

  refresh() {
    if (!this.data) {
      return;
    }
    super.refresh();
    this.drawAxis();
  }

  zoomOut() {
    if (this.width === this.ctx.canvas.clientWidth) {
      return;
    }
    this.width /= 2;
    const zoom = this.width / this.ctx.canvas.clientWidth;
    this.waveformZoomSubject.next({ zoom, width: this.width });
  }

  zoomIn() {
    const { duration, sample_rate, scale: currentScale } = this.audioEditor.waveformData;
    const newScale = Math.floor((duration * sample_rate) / (this.width * 2));
    if (newScale < currentScale) {
      return;
    }
    this.width *= 2;
    const zoom = this.width / this.ctx.canvas.clientWidth;
    this.waveformZoomSubject.next({ zoom, width: this.width });
  }

  drawBackground() {
    this.ctx.fillStyle = this.audioEditor.options.backgroundColor;
    roundRect(
      this.ctx,
      0,
      SPACE_TOP,
      this.ctx.canvas.width,
      this.ctx.canvas.height - SPACE_BOTTOM - SPACE_TOP,
      { tl: 5, tr: 5, bl: 5, br: 5 },
    );
    this.ctx.fill();
  }

  drawWaveform(
    ctx: CanvasRenderingContext2D,
    waveform: WaveformData,
    height: number,
    padding: number,
  ) {
    ctx.beginPath();
    ctx.fillStyle = this.audioEditor.options.waveformColor;

    const channel = waveform.channel(0);
    for (let i = 0; i < ctx.canvas.clientWidth; i += 1) {
      try {
        const index = i + this.offset;
        const y = index >= 0 && index < waveform.length ? channel.min_sample(index) : 0;
        ctx.lineTo(
          i + 0.5,
          SPACE_TOP + scale(y, height - SPACE_BOTTOM - SPACE_TOP, padding),
        );
      } catch (e) {}
    }

    for (let i = ctx.canvas.clientWidth - 1; i >= 0; i -= 1) {
      try {
        const index = i + this.offset;
        const y = index >= 0 && index < waveform.length ? channel.max_sample(index) : 0;
        ctx.lineTo(
          i + 0.5,
          SPACE_TOP + scale(y, height - SPACE_BOTTOM - SPACE_TOP, padding),
        );
      } catch (e) {}
    }

    ctx.closePath();
    ctx.fill();
  }

  drawAxis() {
    this.ctx.fillStyle = this.audioEditor.options.axis.backgroundColor;
    this.ctx.fillRect(0, 0, this.ctx.canvas.width, SPACE_TOP);

    const duration = this.audioEditor.viewBox.to - this.audioEditor.viewBox.from;
    const maxStep = duration / 20;

    let index = 0;
    while (steps[index] < maxStep) {
      index += 1;
    }

    this.ctx.fillStyle = this.audioEditor.options.axis.fontColor;
    this.ctx.strokeStyle = this.audioEditor.options.axis.fontColor;
    const step = steps[index];

    for (let t = 0; t < this.audioEditor.waveformData.duration; t += step) {
      if (t < this.audioEditor.viewBox.from || t > this.audioEditor.viewBox.to) {
        continue;
      }

      const h = Math.floor(t / 3600);
      const mns = Math.floor(t / 60) - 60 * h;
      const secs = Math.floor(t) - 60 * mns - 3600 * h;

      // const mns = Math.floor(t / 60);
      // const secs = Math.floor(t) - 60 * mns;
      // const mls = Math.floor((t % 1) * 1000);
      const str = [h, mns, secs].map(formatNumber).join(':');

      const x = this.timeToPixels(t) - this.offset;
      this.ctx.beginPath();
      this.ctx.moveTo(x, SPACE_TOP - 6);
      this.ctx.lineTo(x, SPACE_TOP);
      this.ctx.stroke();

      const length = this.ctx.measureText(str).width;
      // this.ctx.font = '500 15px Gotham';
      this.ctx.font = '12px Nunito';
      this.ctx.fillText(str, x - length / 2, SPACE_TOP - 10);
    }
  }

  drawCursor(ctx: CanvasRenderingContext2D, instant: number) {
    const x = this.timeToPixels(instant) - this.offset;
    if (x > ctx.canvas.width) {
      return;
    }

    ctx.strokeStyle = this.audioEditor.options.cursorColor;
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.moveTo(x, SPACE_TOP);
    ctx.lineTo(x, ctx.canvas.height - SPACE_BOTTOM);
    ctx.stroke();
    ctx.lineWidth = 1;

    const middleY = (ctx.canvas.height - SPACE_BOTTOM - SPACE_TOP) / 2;

    ctx.fillStyle = this.audioEditor.options.cursorColor;
    ctx.fillRect(x - 2.5, middleY + 15, 5, 30);
  }

  drawSelectionBox(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = this.audioEditor.options.selectionBox.backgroundColor;
    ctx.globalAlpha = 0.5;

    const x1 = this.timeToPixels(this.audioEditor.selectionBox.from) - this.offset;
    const x2 = this.timeToPixels(this.audioEditor.selectionBox.to) - this.offset;

    ctx.fillRect(x1, SPACE_TOP, x2 - x1, ctx.canvas.height - SPACE_BOTTOM - SPACE_TOP);

    ctx.beginPath();
    ctx.fillStyle = this.audioEditor.options.selectionBox.waveformColor;
    ctx.globalAlpha = 1;

    const channel = this.data.channel(0);
    for (let i = x1; i < x2; i += 1) {
      try {
        const index = i + this.offset;
        const y = index >= 0 && index < this.data.length ? channel.min_sample(index) : 0;
        ctx.lineTo(
          i + 0.5,
          SPACE_TOP +
            scale(
              y,
              ctx.canvas.height - SPACE_BOTTOM - SPACE_TOP,
              this.waveformOptions.padding,
            ),
        );
      } catch (e) {}
    }

    for (let i = x2 - 1; i >= x1; i -= 1) {
      try {
        const index = i + this.offset;
        const y = index >= 0 && index < this.data.length ? channel.max_sample(index) : 0;
        ctx.lineTo(
          i + 0.5,
          SPACE_TOP +
            scale(
              y,
              ctx.canvas.height - SPACE_BOTTOM - SPACE_TOP,
              this.waveformOptions.padding,
            ),
        );
      } catch (e) {}
    }

    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = this.audioEditor.options.viewBox.colorBound;
    ctx.fillStyle = this.audioEditor.options.viewBox.colorBound;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x1, SPACE_TOP);
    ctx.lineTo(x1, ctx.canvas.height - 20);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(x1, ctx.canvas.height - 20, 9, 0, 2 * Math.PI, false);
    ctx.fillStyle = this.audioEditor.options.viewBox.colorBound;
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = this.audioEditor.options.viewBox.colorBound;
    ctx.stroke();

    // ctx.fillRect(x1 - 5, ctx.canvas.height - 20, 10, 20);

    ctx.beginPath();
    ctx.moveTo(x2, SPACE_TOP);
    ctx.lineTo(x2, ctx.canvas.height - 20);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(x2, ctx.canvas.height - 20, 9, 0, 2 * Math.PI, false);
    ctx.fillStyle = this.audioEditor.options.viewBox.colorBound;
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = this.audioEditor.options.viewBox.colorBound;
    ctx.stroke();

    // ctx.fillRect(x2 - 5, ctx.canvas.height - 20, 10, 20);

    ctx.lineWidth = 1;
  }

  drawIntroCursor(ctx: CanvasRenderingContext2D, instant: number): void {
    const x = this.timeToPixels(instant) - this.offset;
    if (x > ctx.canvas.width) {
      return;
    }

    ctx.strokeStyle = this.audioEditor.options.intro.color;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(x, SPACE_TOP);
    ctx.lineTo(x, ctx.canvas.height - SPACE_BOTTOM);
    ctx.stroke();
    ctx.lineWidth = 1;

    peach(ctx, x, ctx.canvas.height - SPACE_BOTTOM, this.audioEditor.options.intro.color);
  }

  drawMixCursor(ctx: CanvasRenderingContext2D, instant: number): void {
    const x = this.timeToPixels(instant) - this.offset;
    if (x > ctx.canvas.width) {
      return;
    }

    ctx.strokeStyle = this.audioEditor.options.mix.color;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(x, SPACE_TOP);
    ctx.lineTo(x, ctx.canvas.height - SPACE_BOTTOM);
    ctx.stroke();
    ctx.lineWidth = 1;

    peach(ctx, x, ctx.canvas.height - SPACE_BOTTOM, this.audioEditor.options.mix.color);
  }

  drawOutroCursor(ctx: CanvasRenderingContext2D, instant: number): void {
    const x = this.timeToPixels(instant) - this.offset;
    if (x > ctx.canvas.width) {
      return;
    }

    ctx.strokeStyle = this.audioEditor.options.outro.color;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(x, SPACE_TOP);
    ctx.lineTo(x, ctx.canvas.height - SPACE_BOTTOM);
    ctx.stroke();
    ctx.lineWidth = 1;

    peach(ctx, x, ctx.canvas.height - SPACE_BOTTOM, this.audioEditor.options.outro.color);
  }
}
